import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Mail, Phone, Package } from 'lucide-react';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';

const priceText = (p) => (p == null ? null : `$${Number(p).toFixed(2)}`);

const SPEC_FIELDS = [
  ['caliber', 'Caliber'],
  ['length', 'Length'],
  ['diameter', 'Diameter'],
  ['weight', 'Weight'],
  ['material', 'Material'],
  ['finish', 'Finish'],
  ['mounting_type', 'Mounting'],
  ['included_accessories', 'Included'],
];

const ProductDetailDialog = ({ open, product, brand, category, onClose, onInquire }) => {
  const { business } = useSiteSettings();
  if (!product) return null;
  const specs = SPEC_FIELDS.filter(([k]) => product[k]);
  const price = priceText(product.price);
  const sale = priceText(product.sale_price);
  const showSale = sale && product.sale_price != null && product.price != null && product.sale_price < product.price;
  const action = product.action_type || 'request_info';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="shop-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" data-testid="shop-detail-title">{product.name}</DialogTitle>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">
            {brand?.name || 'TayLord Arms'}{category?.name ? ` · ${category.name}` : ''}
            {product.model ? ` · ${product.model}` : ''}
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-slate-950 rounded overflow-hidden aspect-square flex items-center justify-center">
            {product.main_image_url ? (
              <img src={product.main_image_url} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <Package className="w-16 h-16 text-gray-700" />
            )}
          </div>
          <div className="space-y-4">
            <div className="tabular-nums">
              {showSale ? (
                <>
                  <span className="text-white font-bold text-2xl">{sale}</span>
                  <span className="ml-2 text-gray-500 line-through">{price}</span>
                </>
              ) : price ? (
                <span className="text-white font-bold text-2xl">{price}</span>
              ) : (
                <span className="text-gray-400 text-sm">Call for pricing</span>
              )}
            </div>
            {product.short_description && <p className="text-gray-300">{product.short_description}</p>}
            {product.full_description && (
              <p className="text-gray-400 text-sm whitespace-pre-wrap">{product.full_description}</p>
            )}
            {specs.length > 0 && (
              <div className="border-t border-slate-800 pt-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Specifications</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {specs.map(([k, label]) => (
                    <React.Fragment key={k}>
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="text-gray-200">{product[k]}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}
            {product.warranty_url && (
              <a
                href={product.warranty_url}
                target="_blank"
                rel="noreferrer"
                data-testid="shop-detail-warranty"
                className="inline-block text-xs text-sky-400 hover:text-sky-300 underline"
              >
                View warranty info →
              </a>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {action === 'call' && business?.phone ? (
            <a href={`tel:${business.phone}`} data-testid="shop-detail-call" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md font-medium">
              <Phone className="w-4 h-4" /> Call {business.phone}
            </a>
          ) : (
            <Button data-testid="shop-detail-inquire" onClick={onInquire} className="bg-red-600 hover:bg-red-700 text-white">
              <Mail className="w-4 h-4 mr-2" />
              {action === 'check_availability' ? 'Check Availability' : action === 'contact_purchase' ? 'Contact to Purchase' : 'Request Information'}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-slate-700 text-gray-300 hover:bg-slate-800">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailDialog;
